<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<q-icon name="far fa-comment" class="q-mb-sm" /> {{ $t('messages') }}
		</div>

		<q-table
			:data="messages"
			:columns="columns"
			:pagination.sync="server_pagination"
			@request="request"
			:loading="loading"
			:filter="filter"
			row-key="name"
			:no-data-label="$t('no_data_avaliable')"
			:no-results-label="$t('no_results_found')"
			:loading-label="$t('loading_data')"
			:rows-per-page-label="$t('items_per_page')"
			:rows-per-page-options="[5, 10, 50, 100]"
			binary-state-sort
		>
			<template slot="top" slot-scope="props" :props="props">
				<q-search
					v-model="filter"
					icon="far fa-search"
					:placeholder="$t('search')"
					:debounce="500"
				/>
				<div class="col" />
				<q-btn
					color="secondary"
					icon="far fa-plus"
					:label="$t('new')"
					@click="$router.push({ name: 'admin_message', params: { id: 0 } })"
				/>
			</template>

			<q-tr slot="header" slot-scope="props" :props="props">
				<q-th v-for="col in props.cols" :key="col.name" :props="props">
					{{ $t(col.label) }}
				</q-th>
			</q-tr>

			<q-tr slot="body" slot-scope="props" :props="props">
				<q-td auto-width key="id" :props="props">
					{{ props.row.id }}
				</q-td>

				<q-td key="title" :props="props">
					{{ props.row.title }}
				</q-td>

				<q-td key="permission" :props="props">
					<q-btn
						color="primary"
						text-color="white"
						:label="getQuantity(props.row.permission)"
						no-ripple
					>
						<q-tooltip>
							<p v-if="props.row.permission.areas.length > 0" class="tooltip q-mb-sm">
								{{ $t('areas') }}:
								<span
									v-for="(area, index) in props.row.permission.areas"
									:key="index"
								>
									{{ getArea(area) }}
									<span v-if="props.row.permission.areas.length - 1 > index"
										>,
									</span>
								</span>
							</p>
							<p
								v-if="props.row.permission.profiles.length > 0"
								class="tooltip q-mb-sm"
							>
								{{ $t('profiles') }}:
								<span
									v-for="(profile, index) in props.row.permission.profiles"
									:key="index"
								>
									{{ getUserProfile(profile) }}
									<span v-if="props.row.permission.profiles.length - 1 > index"
										>,
									</span>
								</span>
							</p>
							<p
								v-if="props.row.permission.user_groups.length > 0"
								class="tooltip q-mb-sm"
							>
								{{ $t('user_groups') }}:
								<span
									v-for="(user_group, index) in props.row.permission.user_groups"
									:key="index"
								>
									{{ getUserGroup(user_group) }}
									<span v-if="props.row.permission.user_groups.length - 1 > index"
										>,
									</span>
								</span>
							</p>
						</q-tooltip>
					</q-btn>
				</q-td>

				<q-td key="author" :props="props" auto-width>
					{{ props.row.author }}
				</q-td>

				<q-td key="send_email" :props="props" auto-width>
					<q-icon
						v-if="props.row.send_email"
						name="fal fa-check-circle"
						size="1.5rem"
						color="positive"
					/>
					<q-icon v-else name="fal fa-ban" size="1.5rem" color="negative" />
				</q-td>

				<q-td key="send_body_email" :props="props" auto-width>
					<q-icon
						v-if="props.row.send_body_email"
						name="fal fa-check-circle"
						size="1.5rem"
						color="positive"
					/>
					<q-icon v-else name="fal fa-ban" size="1.5rem" color="negative" />
				</q-td>

				<q-td key="send_date" :props="props">
					{{ props.row.send_date }}
				</q-td>

				<q-td key="qty_users_sent" :props="props">
					{{ props.row.qty_users_sent }}
				</q-td>

				<q-td key="qty_users_read" :props="props">
					{{ props.row.qty_users_read }}
				</q-td>

				<q-td key="status" :props="props" auto-width>
					<q-chip
						small
						:icon="getIconStatus(props.row.status)"
						:color="getColorStatus(props.row.status)"
						class="full-width"
					>
						{{ $t(getStatus(props.row.status)) }}
					</q-chip>
				</q-td>

				<q-td key="actions" :props="props">
					<q-btn
						flat
						round
						color="primary"
						icon="far fa-pencil-alt"
						@click="
							$router.push({
								name: 'admin_message',
								params: { id: props.row.actions },
							})
						"
					>
						<q-tooltip>
							{{ $t('view_or_edit_tooltip') }}
						</q-tooltip>
					</q-btn>

					<q-btn
						flat
						round
						color="negative"
						icon="far fa-trash-alt"
						@click="confirmRemove(props.row.actions)"
					>
						<q-tooltip>
							{{ $t('delete_tooltip') }}
						</q-tooltip>
					</q-btn>
				</q-td>
			</q-tr>
		</q-table>

		<q-dialog v-model="confirm_remove" @ok="removeItem">
			<span slot="title">{{ $t('remove_item') }}</span>

			<span slot="message">{{ $t('confirm_remove_item') }}</span>

			<template slot="buttons" slot-scope="props" :props="props">
				<q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
				<q-btn color="negative" :label="$t('remove')" @click="removeItem()" />
			</template>
		</q-dialog>
	</q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { date } from 'quasar'
const { formatDate } = date
import { LocalStorage } from 'quasar'

export default {
	name: 'AdminMessages',

	i18nOptions: { namespaces: ['custom', 'common', 'messages', 'admin'] },

	data() {
		return {
			server_pagination: {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: null,
				descending: false,
				sortBy: null,
			},
			loading: false,
			messages: [],
			columns: [
				{
					name: 'id',
					required: true,
					label: '#',
					align: 'left',
					field: 'name',
					sortable: false,
				},
				{
					name: 'title',
					required: true,
					label: 'title',
					align: 'left',
					field: 'title',
					sortable: false,
				},
				{
					name: 'permission',
					required: true,
					label: 'send_group',
					align: 'left',
					field: 'permission',
					sortable: false,
				},
				{
					name: 'author',
					required: true,
					label: 'author',
					align: 'left',
					field: 'author',
					sortable: false,
				},
				{
					name: 'send_email',
					required: true,
					label: 'send_email',
					align: 'left',
					field: 'send_email',
					sortable: false,
				},
				{
					name: 'send_body_email',
					required: true,
					label: 'send_body_email',
					align: 'left',
					field: 'send_body_email',
					sortable: false,
				},
				{
					name: 'send_date',
					required: true,
					label: 'send_date',
					align: 'left',
					field: 'send_date',
					sortable: false,
				},
				{
					name: 'qty_users_sent',
					required: true,
					label: 'sent',
					align: 'left',
					field: 'qty_users_sent',
					sortable: false,
				},
				{
					name: 'qty_users_read',
					required: true,
					label: 'read',
					align: 'left',
					field: 'qty_users_read',
					sortable: false,
				},
				{
					name: 'status',
					required: true,
					label: 'status',
					align: 'left',
					field: 'status',
					sortable: false,
				},
				{
					name: 'actions',
					required: true,
					label: '',
					align: 'right',
					field: 'edit',
					sortable: false,
				},
			],
			filter: '',
			action_id: '',
			confirm_remove: false,
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			areas: 'registration_lists/country_areas',
			user_groups: 'registration_lists/group_users',
			profiles: 'registration_lists/profiles',
			num_rows: 'datatable/num_rows',
		}),

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},
	},

	methods: {
		request({ pagination }) {
			this.loading = true
			this.server_pagination = pagination

			let order = pagination.descending ? '-' : ''
			let order_by = pagination.sortBy ? order + pagination.sortBy : ''

			let data = {
				page_size: this.server_pagination.rowsPerPage,
				page: this.server_pagination.page,
				search: this.filter,
				ordering: order_by,
			}

			this.savePagination()

			this.$axios
				.get('/admin_messages/messages/?' + this.$qs.stringify(data), this.getHeader)
				.then((response) => {
					this.server_pagination.rowsNumber = response.data.count
					this.messages = []

					response.data.results.forEach((result) => {
						let permission = {}

						permission.areas = result.areas
						permission.profiles = result.profiles
						permission.user_groups = result.user_groups

						this.messages.push({
							id: result.id,
							title: result.title,
							permission: permission,
							author: result.author ? result.author : '',
							send_date: result.send_date
								? date.formatDate(result.send_date, 'DD/MM/YYYY HH:mm:ss')
								: '',
							send_email: result.send_email,
							send_body_email: result.send_body_email,
							qty_users_sent: result.qty_users_sent,
							qty_users_read: result.qty_users_read,
							status: result.status,
							actions: result.id,
						})
					})

					this.loading = false
				})
				.catch((e) => {
					this.loading = false
					console.log(e)
				})
		},

		fetchLists() {
			this.$q.loading.show()

			this.$axios
				.get('/accounts/get_registration_lists/', this.getHeader)
				.then((response) => {
					this.$store.dispatch('registration_lists/SET_LISTS', [response.data])
					this.$q.loading.hide()

					this.request({
						pagination: this.server_pagination,
					})
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		confirmRemove(id) {
			this.action_id = id
			this.confirm_remove = true
		},

		removeItem() {
			this.$q.loading.show()

			this.$axios
				.delete(`/admin_messages/messages/${this.action_id}/`, this.getHeader)
				.then((response) => {
					this.$q.loading.hide()

					this.request({
						pagination: this.server_pagination,
					})

					this.confirm_remove = false

					this.$q.notify({
						message: this.$t('item_removed_success'),
						type: 'positive',
						icon: 'far fa-trash-alt',
					})
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		getStatus(status) {
			if (status == 1) return 'scheduled'
			else if (status == 2) return 'sent'
			else if (status == 3) return 'deleted'
			else if (status == 4) return 'sending'
			else if (status == 5) return 'send_fail'
		},

		getIconStatus(status) {
			if (status == 1) return 'fas fa-clock'
			else if (status == 2) return 'fas fa-check'
			else if (status == 3) return 'fas fa-trash-alt'
			else if (status == 4) return 'fas fa-hourglass-half'
			else if (status == 5) return 'fas fa-ban'
		},

		getColorStatus(status) {
			if (status == 1) return 'warning'
			else if (status == 2) return 'positive'
			else if (status == 3) return 'negative'
			else if (status == 4) return 'info'
			else if (status == 5) return 'negative'
		},

		getQuantity(permission) {
			return (
				permission.areas.length + permission.user_groups.length + permission.profiles.length
			)
		},

		getArea(area) {
			let obj = this.areas.find((obj) => {
				return obj.value === area
			})
			if (obj) return obj.label
			else return ''
		},

		getUserGroup(user_group) {
			let obj = this.user_groups.find((obj) => {
				return obj.value === user_group
			})
			if (obj) return obj.label
			else return ''
		},

		getUserProfile(profile) {
			let obj = this.profiles.find((obj) => {
				return obj.value === profile
			})
			if (obj) return obj.label
			else return ''
		},

		savePagination() {
			this.$store.dispatch('datatable/SET_NUM_ROWS', [this.server_pagination.rowsPerPage])
		},

		loadPagination() {
			this.server_pagination.rowsPerPage = this.num_rows
		},
	},

	created() {
		this.loadPagination()
		this.fetchLists()
	},
}
</script>

<style lang="stylus" scoped></style>
